import {
  FIELD_LIMITS,
  PLACEHOLDER_EVENT_DATE,
  SCHOOL_TERM_MAX,
  SCHOOL_TERM_MIN,
  SCHOOL_YEAR_MAX,
  SCHOOL_YEAR_MIN,
} from './constants';
import type { EventValidationResult } from './eventValidationTypes';
import { optionalString } from './sanitize';
import type { SchoolTerm } from './types';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidIsoDate = (value: string): boolean => {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
};

const parseTerm = (value: unknown): SchoolTerm | undefined => {
  const term =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : undefined;
  if (
    term === undefined ||
    !Number.isInteger(term) ||
    term < SCHOOL_TERM_MIN ||
    term > SCHOOL_TERM_MAX
  ) {
    return undefined;
  }
  return term as SchoolTerm;
};

const parseYear = (value: unknown): number | undefined => {
  const year =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : undefined;
  if (
    year === undefined ||
    !Number.isInteger(year) ||
    year < SCHOOL_YEAR_MIN ||
    year > SCHOOL_YEAR_MAX
  ) {
    return undefined;
  }
  return year;
};

/** Collect dates from `dates` array and/or legacy single `date`. */
const parseDates = (raw: Record<string, unknown>): string[] => {
  const fromArray = Array.isArray(raw.dates)
    ? raw.dates
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
    : [];

  const fromLegacy =
    typeof raw.date === 'string' && raw.date.trim() !== ''
      ? [raw.date.trim()]
      : [];

  const combined = [...fromArray, ...fromLegacy].filter(
    (value) => value !== PLACEHOLDER_EVENT_DATE,
  );

  return [...new Set(combined)].sort((a, b) => a.localeCompare(b));
};

export const validateEvent = (
  input: unknown,
  options: { requireDate?: boolean; requireDescription?: boolean } = {},
): EventValidationResult => {
  const errors: Record<string, string> = {};
  const raw =
    input && typeof input === 'object'
      ? (input as Record<string, unknown>)
      : {};

  const allowedKeys = new Set([
    'title',
    'description',
    'date',
    'dates',
    'term',
    'year',
    'highlight',
    'imageKey',
  ]);
  for (const key of Object.keys(raw)) {
    if (!allowedKeys.has(key)) {
      errors._form = 'Unexpected field in submission';
      break;
    }
  }

  const title = optionalString(raw.title, FIELD_LIMITS.eventTitle);
  const description = optionalString(
    raw.description,
    FIELD_LIMITS.eventDescription,
  );
  const dates = parseDates(raw);
  const term = parseTerm(raw.term);
  const year = parseYear(raw.year);
  const highlight = raw.highlight === true;
  const imageKey =
    typeof raw.imageKey === 'string' && raw.imageKey.length <= 200
      ? raw.imageKey
      : undefined;

  if (!title) {
    errors.title = 'Please enter a title';
  }
  if (options.requireDescription !== false && !description) {
    errors.description = 'Please enter a description';
  }

  const invalidDates = dates.filter((value) => !isValidIsoDate(value));
  if (invalidDates.length > 0) {
    errors.dates = 'Enter valid dates (YYYY-MM-DD)';
  }

  if (options.requireDate && dates.length === 0) {
    errors.dates = 'At least one date is required to publish';
  }

  const earliestDate = dates[0];

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    sanitized: {
      title: title ?? '',
      description: description ?? '',
      dates: dates.length > 0 ? dates : undefined,
      date: earliestDate,
      term,
      year,
      highlight,
      imageKey,
    },
  };
};
